import React, {CSSProperties, JSX, useEffect, useState} from 'react';
import styles from './_application-form.module.scss';
import { Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import {
  createApplication, setCustomerPortalLinkId,
  setSelectedLanguageId, updateApplication
} from '../../../Store/Shared/actions';
import { useParams } from 'react-router';
import { AppState } from '../../../Store/rootReducer';
import LoadingIndicator from '../../Shared/LoadingIndicator/LoadingIndicator';
import {
  ICustomField,
  splitCamelCaseWord, hasValue, CustomerWithMapping, isValidHtml,
  LanguageSelector, languages, CreditApplication,
  CustomerWithSignature,
  ApplicationEditorModel,
  CreditApplicationStyleConfig,
  Store,
  CreditAppScoringResultPage
} from '@amsanalytics/react-shared-library';
import MessageViewer from '../../Controls/MessageViewer/MessageViewer';
import { httpGetAsync } from '../../../Http/HttpClient';
import { displayError, isGuid } from '../../../Helpers/Utilities';
import { isNil, isNotNil } from 'ramda';
import { ApplicationWithTargetAssignedToId } from '../../../Models/ApplicationWithTargetAssignedToId';
import ApplicationConfirmation from './ApplicationConfirmation';
import { useAppDispatch } from '../../../Store/useAppDispatch';
import { LeadSourceTrackingData } from '../../../Models/LeadSourceParameters';
import { ApplicationSubmission } from '../../../Models/ApplicationSubmission';
import { applicationSources } from '../../../Helpers/Constants';
import {CreateApplicationFromFormResults} from "../../../Models/CreateApplicationFromFormResults";
import {shouldRedirectAutomatically, shouldRedirectToCustomerPortal} from "../../../Utils";

export interface CustomerPortalApplicationData {
  isNew: boolean;
  application: ApplicationEditorModel;
  addAdditionalCustomer: boolean;
  applicationCustomFields: ICustomField[];
}

export interface CreditApplicationPageProps {
  headerAndFooterHeight: number;
  useCuratedSite: boolean;
}

export default function CreditApplicationPage(props: CreditApplicationPageProps) {
  const dispatch = useAppDispatch();
  const params = useParams();
  const clientId = useSelector<AppState, string | null>(appState => appState.shared.clientId);
  const locationId = useSelector<AppState, string | null>(appState => appState.shared.locationId);
  // ID of the employee we should assign the contact's opportunity to
  const targetAssignedToIdForNewApplication = useSelector<AppState, string | null>(appState => appState.shared.targetAssignedToIdForNewApplications);
  const createApplicationFromFormResults = useSelector<AppState, CreateApplicationFromFormResults | null>(appState => appState.shared.createApplicationFromFormResults);
  const customerPortalLinkId = useSelector<AppState, string | null>(appState => appState.shared.customerPortalLinkId);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const housingStatuses = useSelector((appState: AppState) => appState.shared.housingStatuses);
  const incomePeriods = useSelector((appState: AppState) => appState.shared.incomePeriods);
  const incomeTypes = useSelector((appState: AppState) => appState.shared.incomeTypes);
  const disclosure = useSelector((appState: AppState) => appState.shared.disclosure);
  const spanishDisclosure = useSelector((appState: AppState) => appState.shared.spanishDisclosure);
  const maritalStatuses = useSelector((appState: AppState) => appState.shared.maritalStatuses);
  const identificationTypes = useSelector((appState: AppState) => appState.shared.identificationTypes);
  const states = useSelector((appState: AppState) => appState.shared.states);
  const stores: Store[] = useSelector((appState: AppState) => appState.shared.stores);
  const relationshipsToApplicant = useSelector((appState: AppState) => appState.shared.relationshipsToApplicant);
  const privacyPolicyUrl = useSelector<AppState, string | null>(appState => appState.shared.privacyPolicyUrl);
  const termsConditionsUrl = useSelector<AppState, string | null>(appState => appState.shared.termsConditionsUrl);
  const languageTranslations = useSelector((appState: AppState) => appState.shared.languageTranslations);
  const allowSpanishTranslation = useSelector((appState: AppState) => appState.shared.allowSpanishTranslation);
  const selectedLanguageId: '1' | '2' = useSelector((appState: AppState) => appState.shared.selectedLanguageId);
  const applicationStyleConfig = useSelector((appState: AppState) => appState.shared.applicationStyleConfig);
  const creditApplicationApplicantConfiguration = useSelector((appState: AppState) => appState.shared.creditApplicationApplicantConfiguration);
  const creditApplicationCoApplicantConfiguration = useSelector((appState: AppState) => appState.shared.creditApplicationCoApplicantConfiguration);
  const leadSourceTrackingInfo: LeadSourceTrackingData = useSelector((appState: AppState) => appState.shared.leadSourceParameters);
  const showLocationDropdownMenu: boolean = useSelector((appState: AppState) => appState.shared.showLocationOnNewCreditApp);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [isNewApplication, setIsNewApplication] = useState<boolean>(false);
  // const [application, setApplication] = useState<ApplicationEditorModel | null>();
  const [customerPortalApplicationInfo, setCustomerPortalApplicationInfo] = useState<CustomerPortalApplicationData | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(stores.find(store => hasValue(store.amsCode) && store.amsCode.toUpperCase() === locationId?.toUpperCase())?.id ?? null);

  // There are 3 scenarios where the application is set:
  // 1. The application terms have not been accepted by the customer. This will get current application so they can accept the terms.
  // 2. To get the information from an application previously associated with the contact on a different opportunity
  // 3. When there is no current or previous application, we want to gather whatever details from the contact opportunity that can be used to prepopulate the application form.
  const getApplicationForPortalLink = async (customerPortalLinkId: string) => {
    const repsonse: Response | undefined = await httpGetAsync(`Application/getForPortalLink?customerPortalLinkId=${customerPortalLinkId}`);
    // if the response is ok, and the body is not an empty object, then set the application state variable.
    if (repsonse && repsonse.ok) {
      // try plucking the json from the response
      const customerPortalApplication = await repsonse.json();
      // if the application is found, then set the application state variable.
      if (isNotNil(customerPortalApplication)) {
        setCustomerPortalApplicationInfo(customerPortalApplication);
      }
    } else {
      // if the application is not found, then show an error message to the user.
      dispatch(displayError('There was an issue loading your application. Please try refreshing the page.'));
    }
  }

  useEffect(() => {
    const {customerPortalLinkId, customerId} = params;
    // if customer portal link id is present, then try to get the application associated with the customer portal link.
    if (customerPortalLinkId && isGuid(customerPortalLinkId)) {
      if (customerId && isGuid(customerId)) {
        setCustomerId(customerId);
      }
      dispatch(setCustomerPortalLinkId(customerPortalLinkId));
      getApplicationForPortalLink(customerPortalLinkId);
    } else {
      setIsNewApplication(true);
    }
  }, [params]);

  // After submitting an app, redirect user to the appropriate page if necessary.
  useEffect(() => {
    if (shouldRedirectToCustomerPortal(createApplicationFromFormResults, clientId, customerPortalLinkId)) {
      window.location.replace(`${window.location.protocol}//${window.location.host}/${clientId}/portal/${customerPortalLinkId}`);
    }
    // If a custom creditApplicationRedirectUrl if one exists for this client, redirect to it.
    if (shouldRedirectAutomatically(createApplicationFromFormResults) && hasValue(createApplicationFromFormResults?.redirectUrl)) {
      window.location.replace(createApplicationFromFormResults!.redirectUrl!);
    }
  }, [createApplicationFromFormResults, clientId, customerPortalLinkId]);

  const libraryLoaded = (
    incomePeriods.length > 0 &&
    incomeTypes.length > 0 &&
    states.length > 0 &&
    maritalStatuses.length > 0 &&
    housingStatuses.length > 0 &&
    creditApplicationApplicantConfiguration !== null);


  // Returns the wording for the loading indicator based on the selected language and the application state.
  const getLoadingIndicatorWording = () => {
    if (selectedLanguageId === '1') {
      return isNil(createApplicationFromFormResults?.redirectUrl) && submitted ? 'Submitting application...' : 'Loading...';
    } else {
      return isNil(createApplicationFromFormResults?.redirectUrl) && submitted ? 'Enviando la solicitud...' : 'Cargando...';
    }
  }

  const simplifiedCredApplicationLookups = {
    housingStatuses: housingStatuses?.map(housingStatus => ({ id: housingStatus.id, name: splitCamelCaseWord(housingStatus.name), sortOrder: housingStatus.sortOrder, description: housingStatus.description })),
    relationshipTypes: relationshipsToApplicant,
    payPeriods: incomePeriods,
    incomeTypes: incomeTypes,
    maritalStatuses: maritalStatuses,
    identificationTypes: identificationTypes,
    addressStates: states,
    employmentStates: states,
    identificationStates: states,
    translations: languageTranslations,
    stores: stores,
  };
  const customer = customerPortalApplicationInfo?.addAdditionalCustomer
    ? null
    : customerId
      ? customerPortalApplicationInfo?.application?.customers?.find(c => c.id === customerId)
      : customerPortalApplicationInfo?.application?.customers && customerPortalApplicationInfo?.application?.customers?.length > 0
        ? customerPortalApplicationInfo?.application?.customers[0]
        : null;

  const applicationStyle = applicationStyleConfig
    ? JSON.parse(applicationStyleConfig) as CreditApplicationStyleConfig
    : undefined;

  let componentStyle: CSSProperties | undefined = undefined;

  if(applicationStyle?.sectionStyleConfig?.sectionHeaderStyle.backgroundColor) {
    document.documentElement.style.setProperty('--kendo-color-primary', applicationStyle?.sectionStyleConfig?.sectionHeaderStyle.backgroundColor);
    document.documentElement.style.setProperty('--kendo-color-primary-hover', applicationStyle?.sectionStyleConfig?.sectionHeaderStyle.backgroundColor);
    document.documentElement.style.setProperty('--kendo-color-primary-active', applicationStyle?.sectionStyleConfig?.sectionHeaderStyle.backgroundColor);
    document.documentElement.style.setProperty('--kendo-color-primary-emphasis', applicationStyle?.sectionStyleConfig?.sectionHeaderStyle.backgroundColor);
    document.documentElement.style.setProperty('--kendo-color-primary-on-surface', applicationStyle?.sectionStyleConfig?.sectionHeaderStyle.backgroundColor);
  }

  useEffect(() => {
    return () => {
      if(applicationStyle?.sectionStyleConfig?.sectionHeaderStyle.backgroundColor) {
        document.documentElement.style.setProperty('--kendo-color-primary', '#307fbe');
        document.documentElement.style.setProperty('--kendo-color-primary-hover', '#116fa5');
        document.documentElement.style.setProperty('--kendo-color-primary-active', '#106697');
        document.documentElement.style.setProperty('--kendo-color-primary-emphasis', '#4d97c1');
        document.documentElement.style.setProperty('--kendo-color-primary-on-surface', '#1274AC');
      }
    }
  }, []);

  const config = customerPortalApplicationInfo?.addAdditionalCustomer === true
  ? creditApplicationCoApplicantConfiguration ?? creditApplicationApplicantConfiguration
  : creditApplicationApplicantConfiguration;

  // Returns the AMS code of the selectedLocationId
  const getAmsCodeForSelectedLocation = (): string | null => {
    if (!showLocationDropdownMenu || !selectedLocationId) {
      return locationId;
    }
    const selectedLocation = stores.find(store => store.id.toUpperCase() === selectedLocationId.toUpperCase());
    return selectedLocation ? selectedLocation.amsCode : locationId;
  }

  // if the application has been saved and there the redirect URL = '' then we want send a message to the parent iFrame if there is one.
  if (shouldRedirectAutomatically(createApplicationFromFormResults)) {
    window.parent?.postMessage({ event: 'applicationSubmitted' }, '*');
    window.parent?.postMessage({ event: 'redirect', redirectUrl: createApplicationFromFormResults?.redirectUrl }, '*');
  }

  const onNavigateToCuratedSite = () => {
    if (hasValue(createApplicationFromFormResults?.redirectUrl)){
      window.location.replace(createApplicationFromFormResults!.redirectUrl!);
    }
  };
  
  const getApplicationConfirmationComponent = (): JSX.Element => {
    if (createApplicationFromFormResults?.redirectToCustomResultPage) {
      return (
        <CreditAppScoringResultPage
          headerText={createApplicationFromFormResults.headerText ?? ""}
          resultMessage={createApplicationFromFormResults.resultMessage?? ""}
          nextActionText={createApplicationFromFormResults.nextActionText?? ""}
          buttonText={createApplicationFromFormResults.buttonText?? ""}
          onContinue={onNavigateToCuratedSite}
          headerAndFooterHeight={props.headerAndFooterHeight}
        />
      )
    } else {
      return (
        <div style={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <ApplicationConfirmation languageId={selectedLanguageId} />
        </div>
      );
    }
  }
  
  return (
    <>
      {isNotNil(createApplicationFromFormResults?.redirectUrl)
        ? getApplicationConfirmationComponent()
        : (customerPortalApplicationInfo || isNewApplication) && libraryLoaded && !submitted
          ? <div className={styles.applicationFormContainer} style={componentStyle}>
            {allowSpanishTranslation && (
              <LanguageSelector
                selectedLanguageId={selectedLanguageId}
                languages={languages}
                style={{ width: 'fit-content' }}
                label={selectedLanguageId === '1' ? 'Preferred Language' : 'Idioma Preferido'}
                onSelectLanguage={(languageId: string) => {
                  // update browser tab title based on the selected language
                  document.title = languageId === '1' ? 'Customer Credit Application' : 'Solicitud de Crédito del Cliente';
                  dispatch(setSelectedLanguageId(languageId));
                }}
              />
            )}
            <CreditApplication
              trace={false}
              lookups={simplifiedCredApplicationLookups}
              languageId={selectedLanguageId}
              locationId={selectedLocationId}
              onLocationChange={(locationId: string | null) => setSelectedLocationId(locationId)}
              onSubmit={(customer: CustomerWithSignature) => {
                if (clientId === null) {
                  return;
                }
                setSubmitted(true);

                // Updating existing customer that logged into portal via ssn or birth date
                if(customerPortalApplicationInfo && customerId) {
                  const updatedApplication = {
                    ...customerPortalApplicationInfo.application,
                    targetAssignedToId: targetAssignedToIdForNewApplication,
                    customers: customerPortalApplicationInfo.application.customers.map(c => {
                      if (c.id === customerId) {
                        return customer as CustomerWithSignature;
                      }
                      return c;
                    })
                  }
                  dispatch(updateApplication(updatedApplication as ApplicationWithTargetAssignedToId, clientId, customerPortalLinkId!, customerId));
                  return;
                }

                // Adding a new customer to an existing application
                if(customerPortalLinkId && customerPortalApplicationInfo?.addAdditionalCustomer) {
                  const updatedApplication = {
                    ...customerPortalApplicationInfo.application,
                    targetAssignedToId: targetAssignedToIdForNewApplication,
                    customers: [...customerPortalApplicationInfo.application.customers, customer as CustomerWithSignature]
                  }
                  dispatch(updateApplication(updatedApplication as ApplicationWithTargetAssignedToId, clientId, customerPortalLinkId, customerId));
                  return;
                }
                // Creating a new application with customer prepopulated with the contact's information
                if((customerPortalLinkId && customerPortalApplicationInfo?.isNew) || isNewApplication) {
                  const newApplication: ApplicationSubmission = {
                    applicationDate: new Date(),
                    contactOpportunityId: customerPortalApplicationInfo?.application?.contactOpportunityId ?? undefined,
                    targetAssignedToId: targetAssignedToIdForNewApplication,
                    customers: [{...customer, isPrimary: true} as CustomerWithMapping], // Hack to get around the type issue
                    trackingId: hasValue(leadSourceTrackingInfo.trackingData?.trackingId) 
                      ? leadSourceTrackingInfo.trackingData.trackingId 
                      : undefined, // ID reference to tracking data stored in S3
                    source: hasValue(customerPortalLinkId) ? applicationSources.poralLink : applicationSources.websiteEmbed,
                  };
                  dispatch(createApplication(newApplication, clientId, showLocationDropdownMenu ? getAmsCodeForSelectedLocation() : locationId, props.useCuratedSite));
                  return;
                }
              }}
              applicationConfig={config}
              settings={{
                requireSignature: true,
                privacyNoticeUrl: privacyPolicyUrl ?? '',
                termsAndConditionsUrl: termsConditionsUrl ?? '',
                showLocationDropdown: showLocationDropdownMenu,
                disableLocationDropdown: hasValue(locationId) && hasValue(selectedLocationId)
              }}
              customer={
                customer
                ? {
                    ...customer,
                    otherIncomes: customer.otherIncomes?.length > 0
                      ? customer.otherIncomes
                      : [],
                    otherIncomeAndDebt: customer.otherIncomeAndDebt?.length > 0
                      ? customer.otherIncomeAndDebt
                      : []
                  }
                : undefined
              }
              containerStyle={{backgroundColor: 'white'}}
              styleConfig={applicationStyle
                ? applicationStyle
                : {
                    sectionStyleConfig: {
                      sectionHeaderStyle: {borderRadius: '5px 5px 0px 0px', border: `solid 0px rgb(131, 131, 131)`, backgroundColor: 'rgb(0, 108, 196)', color: 'white' },
                      sectionStyle: { backgroundColor: '#c7dbff', marginBottom: '10px', borderRadius: '10px' }
                    },
                  }
              }
            />
            <div style={{ display: 'flex', flexDirection: 'column', padding: '10px' }}>
              {(selectedLanguageId === '2' && spanishDisclosure && isValidHtml(spanishDisclosure))
                ? <div dangerouslySetInnerHTML={{ __html: spanishDisclosure }} />
                : disclosure && isValidHtml(disclosure)
                  ? <div dangerouslySetInnerHTML={{ __html: disclosure }} /> :
                  <Typography style={{ fontSize: '13px', fontWeight: 'bold', color: '#565656' }}>
                    {selectedLanguageId === '2' ? (spanishDisclosure ?? disclosure) : disclosure}
                  </Typography>
              }
            </div>
          </div>
          : <LoadingIndicator label={getLoadingIndicatorWording()} />
      }
      <MessageViewer />
    </>
  );
}
